// ═══════════════════════════════════════════════════════════════════════════════
// TabOptimizacion.jsx — Agente de Optimización de Inventario
// Ubicar en: src/pages/TabOptimizacion.jsx
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from 'react';
import { api } from '../api';

// ─── Configuración de niveles ─────────────────────────────────────────────────
const NIVEL = {
  critico: { color: '#e63946', bg: '#2a0d0d', label: '🔴 CRÍTICO', borde: '#e6394666' },
  alto:    { color: '#ffc107', bg: '#2a1e00', label: '🟡 ALTO',    borde: '#ffc10766' },
  medio:   { color: '#ff9800', bg: '#2a1a00', label: '🟠 MEDIO',   borde: '#ff980066' },
  ok:      { color: '#4caf50', bg: '#0d2218', label: '🟢 OK',      borde: '#4caf5066' },
};

// ─── Mini gráfico de tendencia ────────────────────────────────────────────────
function MiniTrend({ semanas, color }) {
  if (!semanas || semanas.length < 2) return null;
  const max = Math.max(...semanas, 1);
  const w   = 60;
  const h   = 24;
  const pts = semanas.map((v, i) => {
    const x = (i / (semanas.length - 1)) * w;
    const y = h - (v / max) * h;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none"
        stroke={color} strokeWidth={2} strokeLinejoin="round" />
      {semanas.map((v, i) => (
        <circle key={i}
          cx={(i / (semanas.length - 1)) * w}
          cy={h - (v / max) * h}
          r={2.5} fill={color} />
      ))}
    </svg>
  );
}

// ─── Barra de días cobertura ──────────────────────────────────────────────────
function BarraCob({ dias, nivel }) {
  const pct = Math.min((dias / 60) * 100, 100); // 60 días = 100%
  const color = NIVEL[nivel]?.color || '#aaa';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 80, background: '#1a1a2e',
        borderRadius: 3, height: 5, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%',
          background: color, borderRadius: 3,
          transition: 'width .4s' }} />
      </div>
      <span style={{ color, fontFamily: 'monospace',
        fontSize: 12, fontWeight: 700, minWidth: 36 }}>
        {dias < 0 ? '⚠ neg' : dias >= 999 ? '∞' : `${dias}d`}
      </span>
    </div>
  );
}

// ─── Card de producto ─────────────────────────────────────────────────────────
function ProductoCard({ p, expandido, onToggle }) {
  const n   = NIVEL[p.nivel];
  const acel = p.aceleracion;

  return (
    <div style={{
      background: p.nivel === 'ok' ? '#1e1e2e' : n.bg,
      borderRadius: 10,
      border: `1px solid ${n.borde}`,
      borderLeft: `4px solid ${n.color}`,
      overflow: 'hidden',
      transition: 'all .2s',
    }}>
      {/* Fila principal */}
      <div
        onClick={onToggle}
        style={{ display: 'grid',
          gridTemplateColumns: '28px 1fr 90px 90px 110px 80px 80px',
          padding: '12px 14px', gap: 10, alignItems: 'center',
          cursor: 'pointer' }}>

        {/* Nivel badge */}
        <div style={{ fontSize: 16, textAlign: 'center' }}>
          {n.label.split(' ')[0]}
        </div>

        {/* Nombre + código */}
        <div>
          <div style={{ color: '#fff', fontSize: 13,
            fontWeight: 600, lineHeight: 1.3 }}>
            {p.item}
          </div>
          <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
            {p.codigo}
            {acel > 15 && (
              <span style={{ color: '#4caf50', marginLeft: 8,
                fontWeight: 700 }}>
                ▲ +{acel}% esta semana
              </span>
            )}
            {acel < -15 && (
              <span style={{ color: '#e63946', marginLeft: 8,
                fontWeight: 700 }}>
                ▼ {acel}% esta semana
              </span>
            )}
          </div>
        </div>

        {/* Stock */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: p.stock <= 0 ? '#e63946' : '#fff',
            fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>
            {p.stock}
          </div>
          <div style={{ color: '#555', fontSize: 10 }}>en stock</div>
        </div>

        {/* Uds/día */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#4cc9f0', fontFamily: 'monospace',
            fontWeight: 700, fontSize: 14 }}>
            {p.uds_dia}
          </div>
          <div style={{ color: '#555', fontSize: 10 }}>uds/día</div>
        </div>

        {/* Cobertura */}
        <BarraCob dias={p.dias_cobertura} nivel={p.nivel} />

        {/* Tendencia */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <MiniTrend semanas={p.tendencia} color={n.color} />
        </div>

        {/* Flecha */}
        <div style={{ textAlign: 'right', color: '#555', fontSize: 14 }}>
          {expandido ? '▲' : '▼'}
        </div>
      </div>

      {/* Panel expandido */}
      {expandido && (
        <div style={{ borderTop: `1px solid ${n.borde}`,
          padding: '14px 16px',
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 12 }}>
          {[
            { label: 'Vendido últimos 90d', valor: `${p.uds_periodo} uds`, color: '#4cc9f0' },
            { label: 'Precio venta unit.', valor: `$${p.precio.toFixed(2)}`, color: '#4caf50' },
            { label: 'Costo unit.',         valor: `$${p.costo.toFixed(2)}`, color: '#aaa' },
            { label: 'Última venta',         valor: p.ultima_venta || '—', color: '#aaa' },
            { label: '📦 Sugerido pedir',
              valor: `${p.uds_sugerido} uds`,
              color: p.uds_sugerido > 0 ? '#ffc107' : '#4caf50',
              big: true },
            { label: 'Valor pedido estimado',
              valor: `$${p.valor_sugerido.toFixed(2)}`,
              color: '#ffc107' },
          ].map((f, i) => (
            <div key={i} style={{ background: '#0a0a14',
              borderRadius: 7, padding: '10px 12px' }}>
              <div style={{ color: '#555', fontSize: 10,
                textTransform: 'uppercase', letterSpacing: 0.5,
                marginBottom: 4 }}>{f.label}</div>
              <div style={{ color: f.color, fontFamily: 'monospace',
                fontWeight: 700, fontSize: f.big ? 18 : 14 }}>
                {f.valor}
              </div>
            </div>
          ))}

          {/* Barra de tendencia semanal detallada */}
          {p.tendencia?.length > 1 && (
            <div style={{ gridColumn: '1 / -1', background: '#0a0a14',
              borderRadius: 7, padding: '10px 12px' }}>
              <div style={{ color: '#555', fontSize: 10,
                textTransform: 'uppercase', letterSpacing: 0.5,
                marginBottom: 8 }}>Tendencia semanal (últimas {p.tendencia.length} semanas)</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 40 }}>
                {p.tendencia.map((v, i) => {
                  const max = Math.max(...p.tendencia, 1);
                  return (
                    <div key={i} style={{ flex: 1,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 4 }}>
                      <div style={{ color: '#555', fontSize: 9 }}>{v}</div>
                      <div style={{
                        width: '100%',
                        height: `${(v / max) * 32}px`,
                        background: i === p.tendencia.length - 1
                          ? n.color : '#2a2a3e',
                        borderRadius: '2px 2px 0 0',
                        minHeight: 2,
                      }} />
                      <div style={{ color: '#333', fontSize: 8 }}>
                        S{i + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function TabOptimizacion() {
  const [datos,      setDatos]      = useState(null);
  const [cargando,   setCargando]   = useState(false);
  const [dias,       setDias]       = useState(90);
  const [linea,      setLinea]      = useState('');
  const [filtroNivel,setFiltroNivel]= useState('todos'); // todos|critico|alto|medio|ok
  const [expandido,  setExpandido]  = useState(null);
  const [cargado,    setCargado]    = useState(false);

  const cargar = async (d = dias, l = linea) => {
    setCargando(true);
    try {
      const params = `dias=${d}${l ? `&linea=${l}` : ''}`;
      const data = await api.get(`/productos/optimizacion?${params}`);
      setDatos(data.error ? null : data);
      setCargado(true);
    } catch {
      setDatos(null);
    } finally {
      setCargando(false);
    }
  };

  // No carga automáticamente — el usuario activa el análisis
  const productosFiltrados = (datos?.productos || []).filter(p =>
    filtroNivel === 'todos' || p.nivel === filtroNivel
  );

  const FILTROS = [
    { key: 'todos',   label: `Todos (${datos?.total || 0})`,           color: '#aaa' },
    { key: 'critico', label: `🔴 Crítico (${datos?.criticos || 0})`,   color: '#e63946' },
    { key: 'alto',    label: `🟡 Alto (${datos?.altos || 0})`,          color: '#ffc107' },
    { key: 'medio',   label: `🟠 Medio`,                                color: '#ff9800' },
    { key: 'ok',      label: `🟢 OK`,                                   color: '#4caf50' },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: '#e0e0e0' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
            🤖 Agente de Optimización
          </div>
          <div style={{ color: '#555', fontSize: 13, marginTop: 3 }}>
            Analiza rotación real vs stock · Genera alertas de reposición
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={dias}
            onChange={e => setDias(Number(e.target.value))}
            style={{ background: '#1e1e2e', border: '1px solid #2a2a3e',
              borderRadius: 7, color: '#fff', padding: '8px 12px',
              fontSize: 12, outline: 'none' }}>
            <option value={30}>Últimos 30 días</option>
            <option value={60}>Últimos 60 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={180}>Últimos 180 días</option>
          </select>
          <select
            value={linea}
            onChange={e => setLinea(e.target.value)}
            style={{ background: '#1e1e2e', border: '1px solid #2a2a3e',
              borderRadius: 7, color: '#fff', padding: '8px 12px',
              fontSize: 12, outline: 'none' }}>
            <option value="">Todas las líneas</option>
            <option value="ACEITES">Aceites</option>
            <option value="ADITIVOS">Aditivos</option>
            <option value="REFRIGERANTES">Refrigerantes</option>
            <option value="GRASAS">Grasas</option>
          </select>
          <button
            onClick={() => cargar(dias, linea)}
            disabled={cargando}
            style={{ background: cargando ? '#2a2a3e' : '#4361ee',
              border: 'none', borderRadius: 8, color: '#fff',
              padding: '9px 20px', fontSize: 13, fontWeight: 700,
              cursor: cargando ? 'not-allowed' : 'pointer',
              opacity: cargando ? 0.7 : 1 }}>
            {cargando ? '⏳ Analizando...' : cargado ? '🔄 Actualizar' : '🚀 Analizar inventario'}
          </button>
        </div>
      </div>

      {/* ── Estado inicial ─────────────────────────────────────────────────── */}
      {!cargado && !cargando && (
        <div style={{ textAlign: 'center', padding: '60px 0',
          background: '#1e1e2e', borderRadius: 12,
          border: '1px solid #2a2a3e' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
          <div style={{ color: '#aaa', fontSize: 16,
            fontWeight: 600, marginBottom: 8 }}>
            Agente listo para analizar
          </div>
          <div style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>
            Cruza ventas históricas con stock actual para detectar quiebres
          </div>
          <button onClick={() => cargar(dias, linea)} style={{
            background: '#4361ee', border: 'none', borderRadius: 8,
            color: '#fff', padding: '12px 28px',
            fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            🚀 Iniciar análisis
          </button>
        </div>
      )}

      {/* ── Loading ────────────────────────────────────────────────────────── */}
      {cargando && (
        <div style={{ textAlign: 'center', padding: '60px 0',
          background: '#1e1e2e', borderRadius: 12 }}>
          <div style={{ fontSize: 40, marginBottom: 16,
            animation: 'spin 1s linear infinite' }}>⚙️</div>
          <div style={{ color: '#4cc9f0', fontSize: 15 }}>
            Analizando {dias} días de ventas y stock actual...
          </div>
          <div style={{ color: '#555', fontSize: 12, marginTop: 8 }}>
            Cruzando Morpheus ERP con Gemini AI
          </div>
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {cargado && !datos && !cargando && (
        <div style={{ color: '#e63946', textAlign: 'center',
          padding: 40, fontSize: 14 }}>
          Error cargando datos — verifica la conexión al VPS
        </div>
      )}

      {/* ── Resultados ─────────────────────────────────────────────────────── */}
      {datos && !cargando && (
        <>
          {/* KPIs */}
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Productos analizados', valor: datos.total,
                color: '#4cc9f0', bg: '#001a2a' },
              { label: '🔴 Stock crítico',      valor: datos.criticos,
                color: '#e63946', bg: '#2a0d0d' },
              { label: '🟡 Alerta alta',         valor: datos.altos,
                color: '#ffc107', bg: '#2a1e00' },
              { label: 'Con alertas',            valor: datos.alertas,
                color: '#f72585', bg: '#2a0028' },
            ].map((k, i) => (
              <div key={i} style={{ background: k.bg,
                borderRadius: 10, padding: '14px 18px',
                borderTop: `3px solid ${k.color}` }}>
                <div style={{ color: '#555', fontSize: 10,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  marginBottom: 6 }}>{k.label}</div>
                <div style={{ color: k.color, fontFamily: 'monospace',
                  fontWeight: 700, fontSize: 28 }}>{k.valor}</div>
              </div>
            ))}
          </div>

          {/* Análisis IA Gemini */}
          {datos.analisis_ia && (
            <div style={{ background: '#0a1428',
              border: '1px solid #4361ee44',
              borderLeft: '4px solid #4361ee',
              borderRadius: 10, padding: '16px 20px',
              marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center',
                gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>✨</span>
                <span style={{ color: '#4361ee', fontWeight: 700,
                  fontSize: 13 }}>Análisis Gemini AI</span>
                <span style={{ color: '#333', fontSize: 11 }}>
                  · basado en {datos.dias} días de ventas reales
                </span>
              </div>
              <div style={{ color: '#ccc', fontSize: 13,
                lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {datos.analisis_ia}
              </div>
            </div>
          )}

          {/* Filtros de nivel */}
          <div style={{ display: 'flex', gap: 6,
            marginBottom: 14, flexWrap: 'wrap' }}>
            {FILTROS.map(f => (
              <button key={f.key}
                onClick={() => setFiltroNivel(f.key)}
                style={{
                  background: filtroNivel === f.key
                    ? `${f.color}22` : '#1e1e2e',
                  border: `1px solid ${filtroNivel === f.key
                    ? f.color : '#2a2a3e'}`,
                  borderRadius: 7, color: filtroNivel === f.key
                    ? f.color : '#555',
                  padding: '6px 14px', fontSize: 12,
                  fontWeight: filtroNivel === f.key ? 700 : 400,
                  cursor: 'pointer',
                }}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Header tabla */}
          <div style={{ display: 'grid',
            gridTemplateColumns: '28px 1fr 90px 90px 110px 80px 80px',
            padding: '8px 14px', gap: 10,
            color: '#555', fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5,
            marginBottom: 4 }}>
            <span />
            <span>Producto</span>
            <span style={{ textAlign: 'right' }}>Stock</span>
            <span style={{ textAlign: 'right' }}>Uds/día</span>
            <span>Cobertura</span>
            <span style={{ textAlign: 'center' }}>Tendencia</span>
            <span />
          </div>

          {/* Lista de productos */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {productosFiltrados.length === 0 ? (
              <div style={{ color: '#4caf50', textAlign: 'center',
                padding: 40, fontSize: 14 }}>
                ✅ Sin productos en este nivel de alerta
              </div>
            ) : productosFiltrados.map((p, i) => (
              <ProductoCard
                key={p.codigo}
                p={p}
                expandido={expandido === p.codigo}
                onToggle={() => setExpandido(
                  expandido === p.codigo ? null : p.codigo
                )}
              />
            ))}
          </div>

          {/* Nota */}
          <div style={{ color: '#333', fontSize: 10,
            textAlign: 'right', marginTop: 16 }}>
            Sugerido pedir = cubrir 30 días + 20% buffer · stock en Bodega Estación
          </div>
        </>
      )}
    </div>
  );
}
